'use client';

import { useQuery } from '@tanstack/react-query';

/**
 * Chart Data Types
 */
export interface PieChartDataPoint {
  name: string;
  value: number;
  color: string;
  [key: string]: unknown;
}

export interface LineChartDataPoint {
  date: string;
  inspections: number;
}

export interface DashboardChartData {
  jobStatusBreakdown: PieChartDataPoint[];
  inspectionsOverTime: LineChartDataPoint[];
}

export interface ChartFilters {
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  status?: string[];
}

import { apiClient } from '@/lib/api-client';

/**
 * Fetch real chart data from backend (CR-006-U157)
 */
const fetchChartData = async (filters?: ChartFilters): Promise<DashboardChartData> => {
  try {
    // Fetch job status breakdown and time-series data in parallel
    const [statusData, timeSeriesData] = await Promise.all([
      apiClient.getTenantJobStatusBreakdown({
        ...(filters?.dateRange?.startDate !== undefined ? { start_date: filters.dateRange.startDate } : {}),
        ...(filters?.dateRange?.endDate !== undefined ? { end_date: filters.dateRange.endDate } : {}),
      }),
      apiClient.getTenantTimeSeriesAnalytics({
        metric: 'jobs',
        interval: 'day',
        start_date: filters?.dateRange?.startDate ||
          (new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? ''), // Last 7 days
        ...(filters?.dateRange?.endDate !== undefined ? { end_date: filters.dateRange.endDate } : {}),
      }),
    ]);

    // Transform job status breakdown
    const jobStatusBreakdown: PieChartDataPoint[] = statusData.breakdown.map((item: {name?: string; status?: string; count?: number; value?: number; date?: string; color?: string}) => ({
      name: item.name,
      value: item.value,
      color: item.color,
    }));

    // Transform time-series data
    const inspectionsOverTime: LineChartDataPoint[] = timeSeriesData.data.map((item: {name?: string; status?: string; count?: number; value?: number; date?: string; color?: string}) => ({
      date: item.date,
      inspections: item.value,
    }));

    return {
      jobStatusBreakdown,
      inspectionsOverTime,
    };
  } catch (error) {
    console.error('Failed to fetch chart data:', error);
    // Return empty data on error
    return {
      jobStatusBreakdown: [],
      inspectionsOverTime: [],
    };
  }
};

/**
 * useDashboardChartData Hook
 *
 * TanStack Query hook for fetching dashboard chart data.
 * Supports optional filters for date range and status.
 *
 * @param filters - Optional filters for date range and status
 * @returns Query result with chart data, loading, and error states
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDashboardChartData({
 *   dateRange: {
 *     startDate: '2025-01-01',
 *     endDate: '2025-01-31',
 *   },
 *   status: ['pending', 'in-progress'],
 * });
 * ```
 */
export function useDashboardChartData(filters?: ChartFilters) {
  return useQuery({
    queryKey: ['dashboard-charts', filters],
    queryFn: async (): Promise<DashboardChartData> => {
      // Fetch real data from backend (CR-006-U157)
      return fetchChartData(filters);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}
